import { type BdsSelectItemRendererProps, type RadioWithArgs } from "@buildertrend/components";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

export function mapBTSelectItemsToRadioArray<ExtraDataType>(
    btSelectItems: BTSelectItem<ExtraDataType>[],
) {
    const items: RadioWithArgs[] = [];
    let selectedItemId = "";

    for (const item of btSelectItems) {
        if (item.children) {
            for (const child of item.children) {
                items.push({ label: child.title, value: child.id.toString() });
                if (child.selected) selectedItemId = child.id.toString();
            }
        } else {
            items.push({ label: item.title, value: item.id.toString() });
            if (item.selected) selectedItemId = item.id.toString();
        }
    }

    return { items, selectedItemId };
}

export const deselectArrayElements = (arr: BdsSelectItemRendererProps[], newItemValue: number) => {
    arr.forEach((el) => {
        if (el.value !== newItemValue) {
            el.selected = false;
        }
    });
};

export const sortAlphabetically = (
    a: BdsSelectItemRendererProps,
    b: BdsSelectItemRendererProps,
) => {
    const aText = a.text.toUpperCase();
    const bText = b.text.toUpperCase();

    if (aText < bText) {
        return -1;
    } else if (aText > bText) {
        return 1;
    } else {
        return 0;
    }
};

export function convertToSentenceCase(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Recursively merges properties from the source object into the target object.
 *
 * For each property in the source:
 * - If the value is a plain object (i.e., non-null and not an Array), the function recurses into that object to merge its properties.
 * - Otherwise (if the value is a primitive, an array, or any other non-object), the property on the target is overwritten.
 *
 * **Note:** This function mutates the target object in place. Arrays are not deep-copied but simply replaced.
 *
 * @param {Record<string, any>} target - The object into which properties will be merged (modified in place).
 * @param {Record<string, any>} source - The object from which properties are copied.
 */
export function mergeFormData(target: Record<string, any>, source: Record<string, any>) {
    for (const [key, value] of Object.entries(source)) {
        if (value !== null && typeof value === "object" && !Array.isArray(value)) {
            // if the value is a plain object (but not an array), merge recursively.
            if (
                typeof target[key] !== "object" ||
                target[key] === null ||
                Array.isArray(target[key])
            ) {
                target[key] = {};
            }
            mergeFormData(target[key], value);
        } else {
            // for primitives or arrays, overwrite the target's value.
            target[key] = value;
        }
    }
}
